import { IBooth } from "../store/interfaces/IBooth";
import {
  CAMERA_OFFSETS,
  CAMERA_ROTATION_POSITION_OFFSETS,
} from "../configs/CAMERA_CONFIG";
import { Vector3 } from "three";
import gsap from "gsap";
import { handleBoothUnhover } from "../helpers/handleBoothHoverChanges";
import { useComponentLoadingStateStore } from "../store/useComponentLoadingStateStore";

const target = new Vector3();
const findBoothPosition = (booth: IBooth) => {
  if (!booth) return;
  return booth.getWorldPosition(target);
};

interface IMobileOffsets {
  x: number;
  y: number;
  z: number;
}

const animateBoothsInTheMiddle = (
  camera: THREE.Camera,
  boothWorldPosition: Vector3,
  boothSize: string,
  shouldRotate: boolean,
  isMobile: boolean,
  mobileOffsets: IMobileOffsets
) => {
  const tl = gsap.timeline({
    onComplete: () => {
      useComponentLoadingStateStore.setState({
        isAnimateToBoothFinished: true,
      });
    },
  });
  tl.to(
    camera.position,
    boothSize === "large"
      ? {
          x: boothWorldPosition.x + CAMERA_OFFSETS.x,
          y: isMobile ? mobileOffsets.y : CAMERA_OFFSETS.y,
          z: boothWorldPosition.z + CAMERA_OFFSETS.z,
          duration: 1,
          delay: 0.2,
          ease: "power4.out",
        }
      : {
          x: boothWorldPosition.x + 1.5,
          y: isMobile ? mobileOffsets.y : CAMERA_OFFSETS.y,
          z: boothWorldPosition.z + 15,
          duration: 1,
          delay: 0.2,
          ease: "power4.out",
        },
    0
  );
  if (!shouldRotate) return;
  tl.to(
    camera.rotation,
    {
      x: CAMERA_ROTATION_POSITION_OFFSETS.middle.rotation.x,
      y: CAMERA_ROTATION_POSITION_OFFSETS.middle.rotation.y,
      z: CAMERA_ROTATION_POSITION_OFFSETS.middle.rotation.z,
      duration: 0.8,
    },
    0.2
  );
};
const animateBoothsToTheRight = (
  camera: THREE.Camera,
  boothWorldPosition: Vector3,
  boothSize: string,
  shouldRotate: boolean,
  isMobile: boolean,
  mobileOffsets: IMobileOffsets
) => {
  const tl = gsap.timeline({
    onComplete: () => {
      useComponentLoadingStateStore.setState({
        isAnimateToBoothFinished: true,
      });
    },
  });
  tl.to(
    camera.position,
    boothSize === "large"
      ? {
          x: boothWorldPosition.x - 7,
          y: isMobile ? mobileOffsets.y : CAMERA_OFFSETS.y,
          z: boothWorldPosition.z + 16,
          duration: 1,
          delay: 0.2,
          ease: "power4.out",
        }
      : {
          x: boothWorldPosition.x - 3.5,
          y: isMobile ? mobileOffsets.y : CAMERA_OFFSETS.y,
          z: boothWorldPosition.z + 14,
          duration: 1,
          delay: 0.2,
          ease: "power4.out",
        },

    0
  );
  if (!shouldRotate) return;
  tl.to(
    camera.rotation,
    {
      x: CAMERA_ROTATION_POSITION_OFFSETS.right.rotation.x,
      y: CAMERA_ROTATION_POSITION_OFFSETS.right.rotation.y,
      z: CAMERA_ROTATION_POSITION_OFFSETS.right.rotation.z,
      duration: 0.8,
    },
    0.2
  );
};
const animateBoothsToTheLeft = (
  camera: THREE.Camera,
  boothWorldPosition: Vector3,
  boothSize: string,
  shouldRotate: boolean,
  isMobile: boolean,
  mobileOffsets: IMobileOffsets
) => {
  const tl = gsap.timeline({
    onComplete: () => {
      useComponentLoadingStateStore.setState({
        isAnimateToBoothFinished: true,
      });
    },
  });
  tl.to(
    camera.position,
    boothSize === "large"
      ? {
          x: boothWorldPosition.x + 5,
          y: isMobile ? mobileOffsets.y : CAMERA_OFFSETS.y,
          z: boothWorldPosition.z + 17,
          duration: 1,
          delay: 0.2,
          ease: "power4.out",
        }
      : {
          x: boothWorldPosition.x + 6.5,
          y: isMobile ? mobileOffsets.y : CAMERA_OFFSETS.y,
          z: boothWorldPosition.z + 14,
          duration: 1,
          delay: 0.2,
          ease: "power4.out",
        },
    0
  );
  if (!shouldRotate) return;
  tl.to(
    camera.rotation,
    {
      x: CAMERA_ROTATION_POSITION_OFFSETS.left.rotation.x,
      y: CAMERA_ROTATION_POSITION_OFFSETS.left.rotation.y,
      z: CAMERA_ROTATION_POSITION_OFFSETS.left.rotation.z,
      duration: 0.8,
    },
    0.2
  );
};
export const animateToBooth = (
  camera: THREE.Camera,
  booth: IBooth,
  boothSize: string,
  shouldRotate = false,
  isMobileBrowser: boolean
) => {
  const mobileOffsets = {
    x: 0,
    y: 3,
    z: 15,
  };
  useComponentLoadingStateStore.setState({ isBrochurePartActive: false });
  handleBoothUnhover(undefined, booth.children[0].children[0]);
  if (booth === undefined || camera === undefined || !boothSize) return;
  useComponentLoadingStateStore.setState({ isAnimateToBoothFinished: false });
  const boothWorldPosition = findBoothPosition(booth);
  if (!boothWorldPosition) return;
  if (booth.position.z === -60 && booth.position.x !== 0) {
    animateBoothsInTheMiddle(
      camera,
      boothWorldPosition,
      boothSize,
      shouldRotate,
      isMobileBrowser,
      mobileOffsets
    );
    return;
  }
  if (booth.position.x === 0) {
    animateBoothsInTheMiddle(
      camera,
      boothWorldPosition,
      boothSize,
      shouldRotate,
      isMobileBrowser,
      mobileOffsets
    );
    return;
  }
  if (booth.position.x > 0) {
    animateBoothsToTheRight(
      camera,
      boothWorldPosition,
      boothSize,
      shouldRotate,
      isMobileBrowser,
      mobileOffsets
    );
    return;
  }
  if (booth.position.x < 0) {
    animateBoothsToTheLeft(
      camera,
      boothWorldPosition,
      boothSize,
      shouldRotate,
      isMobileBrowser,
      mobileOffsets
    );
    return;
  }
};
